"use client";

import { useEffect, useRef, type RefObject } from "react";

import { Button } from "@tripcart/traveler-ui/components/button";
import { cx } from "@tripcart/traveler-ui/cx";

import { useFormatMoney } from "@/lib/currency-format-context";
import { usePluralTranslator, useTranslator } from "@/lib/translation-context";

import type {
  OnRequestConfigDto,
  StorefrontExternalBookingDto,
} from "@tripcart/shared";

import { ExternalBookButton } from "@/components/external-book-button";
import { BarPrice, BarQuotePrice } from "@/components/sticky-book-bar-price";

import type { PartyTotals } from "@/lib/party-totals";

import styles from "./sticky-book-bar.module.css";

/** The bar's own height, reserved on `<body>` so it never covers the end of the
 *  page. `--tc-tour-bookbar-height` is the bar's OWN measured height (see the
 *  `ResizeObserver` below) — 64px until proven otherwise, and genuinely more on
 *  the rare row whose content needed `.inner`'s `min-height` to grow past it.
 *  `--tc-tour-bars-inset` is the bottom TAB bar's height when that bar
 *  renders and unset when it does not, so the fallback covers both. Same
 *  `@media (width < 64rem)` as the `lg:hidden` below — a desktop page has no
 *  fixed bar and must reserve nothing. */
const BOOK_BAR_INSET_CSS =
  `@media (width < 64rem){` +
  `body{padding-bottom:calc(var(--tc-tour-bookbar-height, 64px) + var(--tc-tour-bars-inset, env(safe-area-inset-bottom,0px)))}` +
  `}`;

/**
 * A PLAIN `<style>` for the `<body>` reservation — no `href`, no `precedence`.
 *
 * With `href`+`precedence` React hoists it and, on unmount, leaves it in
 * `<head>` forever: a client-side navigation off this route would take the bar
 * away and keep its padding, and it would fight `mobile-quicknav`'s own
 * `body{padding-bottom}` at the same precedence, the winner decided by which
 * page you opened second.
 *
 * Setting it from an effect leaks nothing but arrives after hydration, which on
 * this bar is a measured layout shift rather than a theoretical one — see the
 * note in `_tour-bottom-tabs.client.tsx`.
 *
 * A plain `<style>` is in the SSR HTML and is removed on unmount, which is both
 * things at once.
 */

interface StickyBookBarProps {
  /**
   * Optional headline price for the bar. When provided (and the experience
   * is configured), the bar reads "From $X" + "Book now". When absent, it
   * reads "Pick your date" + "Choose date" — same scroll target either way.
   */
  fromPrice?: { amount: number; currencyCode: string };
  /** The was-price for the HEADLINE figure (`priceFromWas`), struck through
   *  beside it. Absent when the operator set none — never derived, and never a
   *  rounded-up invention.
   *
   *  Renders only when `partyTotals` is null, because it belongs to
   *  `fromPrice` and to nothing else: striking it through beside the rail's
   *  live figure would compare two numbers that answer different questions,
   *  which is the drift the currency guard below exists to stop. When the rail
   *  has a figure it supplies its OWN was — `originalTotalMinor`. */
  wasPrice?: { amount: number; currencyCode: string };
  /** The operator's own per-line — `headlinePriceLabel`, e.g. "per person" or
   *  "twin-share". The guide's `.bb-amt i`. Omitted renders nothing rather than
   *  guessing "per person", which is wrong on any tour priced per group.
   *
   *  Qualifies ANY one-unit figure, so it rides the rail's "Starting from" too
   *  — that branch fires only at a single head, so the figure under it is one
   *  person's price and needs the same words. It is suppressed on a party
   *  total, where "per person" beside a whole party's charge would be a lie. */
  priceSuffix?: string;
  /**
   * The operator hasn't finished setting up pricing / availability, so the
   * sidebar shows "Booking opens soon" instead of the booking form. Takes
   * precedence over `fromPrice`: an experience with a headline price but an
   * empty availability window is still not bookable, so the bar reads
   * "Booking opens soon" + "Ask a question" rather than inviting a date pick.
   */
  notConfigured?: boolean;
  /**
   * What the booking rail is charging right now, if it has reported yet.
   *
   * When present the bar shows THAT — "Total / <figure> / N travellers", the
   * mobile guide's `.bb-amt`. When absent (no priced category, or the rail has
   * not mounted) it falls back to the from-price. The bar never computes a
   * total itself: a second implementation of this page's money maths is the
   * shape of every pricing bug it has had.
   */
  partyTotals?: PartyTotals | null;
  /** The tour books on the agency's own site: the CTA is a link to that page,
   *  or the operator's widget HTML. */
  externalBooking?: StorefrontExternalBookingDto | null;
  /**
   * Tapped when the user hits the CTA. Typically the parent scrolls the
   * booking sidebar (or the date picker) into view.
   */
  onBook: () => void;
  /** AC-11 — on an on-request tour there is no date-based price or booking
   *  flow to promise, so the bar's price half and its button both replace
   *  their normal content with the quote box's own copy. `undefined` on
   *  every other pricing mode. */
  onRequest?: OnRequestConfigDto;
}

/**
 * Mobile / tablet bottom-fixed booking CTA. Hidden from `lg`, where the
 * desktop sticky rail takes over.
 *
 * ALWAYS ON SCREEN, from the first paint. It used to slide in only once an
 * IntersectionObserver saw a sentinel below the hero leave the viewport, on
 * the reasoning that the inline booking card was already on screen until then
 * so the bar would be a second copy of the same CTA.
 *
 * That reasoning died when the rail moved. On a phone the booking card now
 * lives inside Dates & prices, thousands of pixels down, so between the top of
 * the page and that section there was NO way to book at all — the one bar that
 * exists to solve exactly that was hidden for the whole stretch where it was
 * needed. The mobile guide pins its `.bookbar` from scroll 0 for the same
 * reason.
 */
export function StickyBookBar({
  fromPrice,
  wasPrice,
  priceSuffix,
  notConfigured = false,
  partyTotals = null,
  externalBooking = null,
  onBook,
  onRequest,
}: StickyBookBarProps) {
  const formatMoney = useFormatMoney();
  const t = useTranslator();
  const tn = usePluralTranslator();
  const { priceLabel, wasLabel } = resolveBarPriceLabels({
    notConfigured,
    fromPrice,
    wasPrice,
    formatMoney,
  });
  const barRef = useRef<HTMLDivElement>(null);
  useBookBarHeightPublisher(barRef);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: BOOK_BAR_INSET_CSS }} />
      {/* No `inert` and no off-screen transform: the bar is never off-screen now, so there is no hidden-but-focusable state to defend against. */}
      <div ref={barRef} className={styles.bar}>
        {/* The mobile guide's `.bookbar` is 64px, against the 56px this used to be. The extra 8px is what buys the overline back (see below). */}
        <div className={styles.inner}>
          {onRequest ? (
            <BarQuotePrice
              onRequest={onRequest}
              formatMoney={formatMoney}
              t={t}
            />
          ) : (
            <BarPrice
              notConfigured={notConfigured}
              priceLabel={priceLabel}
              wasLabel={wasLabel}
              priceSuffix={priceSuffix}
              party={notConfigured ? null : partyTotals}
              formatMoney={formatMoney}
              t={t}
              tn={tn}
            />
          )}
          <BarCta
            externalBooking={externalBooking}
            label={
              onRequest
                ? onRequest.buttonLabel || t("Request a Quote")
                : ctaLabel({ notConfigured, hasPrice: !!priceLabel, t })
            }
            // Only an operator's own words need to compete with the price
            // side for room — our own built-in labels ("Check Dates",
            // "Ask a Question") are short and bounded by translation review,
            // and should never lose a tug-of-war against a long price
            // suffix. See `.ctaShrinkable` in the stylesheet.
            //
            // `externalBooking?.type === "link"` and not just
            // `Boolean(externalBooking)`: "html" mode renders the operator's
            // own widget markup (`ExternalBookingHtml`, a `<div>`) in place of
            // the button entirely, which `.ctaShrinkable` — written for a
            // button or an anchor — does not match. Applying the class there
            // was inert, not harmful, but it claimed a shrink behavior that
            // widget mode has no button to apply it to.
            shrinkable={externalBooking?.type === "link" || Boolean(onRequest)}
            onBook={onBook}
          />
        </div>
      </div>
    </>
  );
}

/** Publishes this bar's own measured height, for `<body>`'s reservation above
 *  (`BOOK_BAR_INSET_CSS`) to read instead of assuming the common 64px.
 *  `.inner`'s `min-height` lets the bar grow on a row whose content genuinely
 *  needs more than that — without this, `<body>` would keep reserving only
 *  64px and the page's last few pixels would tuck under the grown bar.
 *  `ResizeObserver` on the bar itself, not a window listener: it fires for a
 *  font swap or a translation change too, the same reasoning
 *  `_tour-tabs-bar.client.tsx` gives for its own `--tc-tour-marker`. */
function useBookBarHeightPublisher(barRef: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const publish = () => {
      document.documentElement.style.setProperty(
        "--tc-tour-bookbar-height",
        `${bar.getBoundingClientRect().height}px`,
      );
    };
    publish();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(publish);
    observer.observe(bar);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--tc-tour-bookbar-height");
    };
  }, [barRef]);
}

/** The bar's button: the external booking (a link or the operator's widget
 *  HTML) for an external tour; otherwise the scroll to the rail.
 *
 *  `shrinkable` marks a label that can be long enough to need the squeeze-
 *  fairly-against-price treatment (the operator's own words); our own
 *  built-in labels get `flex-shrink: 0` instead, so a long price-side
 *  suffix never truncates a short, fixed label like "Check Dates" — see
 *  `.ctaShrinkable` in the stylesheet. */
function BarCta({
  externalBooking,
  label,
  shrinkable,
  onBook,
}: {
  externalBooking: StorefrontExternalBookingDto | null;
  label: string;
  shrinkable: boolean;
  onBook: () => void;
}) {
  const className = cx(styles.cta, shrinkable && styles.ctaShrinkable);
  if (externalBooking) {
    return (
      <ExternalBookButton booking={externalBooking} className={className} />
    );
  }
  return (
    <Button
      type="button"
      size="lg"
      // `variant="book"` — the storefront's booking CTA colour. DESIGN.md
      // §6.1 named the brand primary for this bar, and that is what put the one
      // CTA a phone always has on screen in a different colour from the "Book
      // now" on every departure row it scrolls past. Both rows of §6.1 now say
      // `variant="book"`.
      variant="book"
      onClick={onBook}
      // `size="lg"` is 44px, the touch-target floor.
      className={className}
    >
      {/* Wrapped so a long operator-set label truncates with an ellipsis
          instead of squeezing the price side off the bar — see `.ctaLabel`. */}
      <span className={styles.ctaLabel}>{label}</span>
    </Button>
  );
}

/** Not-configured wins over price: a priced experience with an empty
 *  availability window is still not bookable, so suppress the price. Only
 *  ever beside a real price, and only when it is actually higher — a
 *  struck-through figure that matches or undercuts the one next to it is a
 *  discount claim that is not true.
 *
 *  The currency codes must MATCH before the amounts are compared.
 *  `formatMoney` takes minor units and reads the currency from context, so
 *  comparing a `wasPrice` in one currency against a `fromPrice` in another
 *  compares two raw magnitudes and then prints both as if they were the
 *  third — which is how a struck-through figure ends up claiming a discount
 *  that does not exist. Both figures come from one experience today, so this
 *  is a guard against drift rather than a live bug. */
function resolveBarPriceLabels({
  notConfigured,
  fromPrice,
  wasPrice,
  formatMoney,
}: {
  notConfigured: boolean;
  fromPrice?: { amount: number; currencyCode: string };
  wasPrice?: { amount: number; currencyCode: string };
  formatMoney: (minor: number) => string;
}): { priceLabel: string | null; wasLabel: string | null } {
  const priceLabel =
    !notConfigured && fromPrice ? formatMoney(fromPrice.amount) : null;
  const wasLabel =
    priceLabel &&
    wasPrice &&
    fromPrice &&
    wasPrice.currencyCode === fromPrice.currencyCode &&
    wasPrice.amount > fromPrice.amount
      ? formatMoney(wasPrice.amount)
      : null;
  return { priceLabel, wasLabel };
}

/** What the one always-on-screen button promises.
 *
 *  "Check dates", not "Book now", and that is a correctness fix rather than a
 *  copy preference: the button does not book anything. It scrolls to the date
 *  picker (`handleStickyBarBook`), which is the mobile guide's `.bookbar` label
 *  too. "Book now" on a control that only moves the page is a promise the tap
 *  does not keep — and the departure rows further down DO book, with the same
 *  words, so one page said "Book now" for two different outcomes.
 *
 *  A function rather than a nested ternary in the JSX: the three answers are
 *  the bar's whole decision, and inlining them put the component over the
 *  complexity cap without making anything clearer. Each `t()` call is a
 *  literal, which is what `pnpm i18n` scrapes.
 */
function ctaLabel({
  notConfigured,
  hasPrice,
  t,
}: {
  notConfigured: boolean;
  hasPrice: boolean;
  t: (key: string) => string;
}): string {
  if (notConfigured) return t("Ask a Question");
  return hasPrice ? t("Check Dates") : t("Choose Date");
}
